import { getFinalDesignLicenseStatus, groupBy, s3FileUrlGetter } from './CommonUtils';
import { getLatestRecordBasedOnField } from './CommonUtils.misc';
import { getApparelImages, shouldUsePromoApparelImages } from './ApparelUtils';
import {
  ORIGINAL_VERSION,
  type DesignLicenseStatusTypes,
  type HqLicensingStatusTypes,
} from '@/constants/Proof';
import {
  designLicenseComments,
  designLicenseTagConfig,
  needChangesTooltipMsg,
  needsChangesWorkWithYouMsg,
} from '@/constants/DesignLicense';
import { LICENSING_STATUS_PILL_STYLES } from '@/constants/LicensingStatus';
import type {
  DesignLicenseTagConfigType,
  IDesignCard,
  IDesignGroupColorOption,
  IDesignGroupProductOption,
  IDesignItem,
  IDesignLicenses,
  IDesignLicensing,
  IDesignProofItem,
  IDesignResponseData,
  IVariantSnapshot,
} from '@/common/interfaces/design.interface';
import { MostRecentActivityStatus } from '@/modules/MostRecent/MostRecentActivity';
import { ViewPort } from '@/common/context/ViewPort/ViewPort';
import type IViewPortData from '@/common/context/ViewPort/ViewPortData';
import type { IOrderItemInfo } from '@/common/interfaces/order.interface';
import type { IDecoratingItem, IPrintTypeItem } from '@/common/interfaces/print-decor.interface';

/**
 * Method to get the proof items and update the proof item with original or revised proof item.
 * It also return the data in a sorted order.
 *
 * @param data - The design response.
 */
export const getDesignItems = (data: IDesignResponseData, proofItemIdKey: string) => {
  const unSortedProofItemIds = Object.keys(groupBy(data.proofItems, proofItemIdKey));

  let proofItemIds = [...unSortedProofItemIds].sort((a: string, b: string) => (a < b ? 1 : -1));
  proofItemIds = proofItemIds.sort((a: string, b: string) => b.length - a.length);

  const proofItemsObj: Record<string, object> = {};
  const unSortedProofItems = groupBy(data.proofItems, proofItemIdKey);

  proofItemIds.forEach((proofItemId: string) => {
    proofItemsObj[proofItemId] = unSortedProofItems[proofItemId];
  });

  const groupedProofItems = JSON.parse(JSON.stringify(proofItemsObj));

  Object.keys(groupedProofItems).forEach((proofItemId: string) => {
    groupedProofItems[proofItemId]
      .sort((a: Record<string, number>, b: Record<string, number>) => a.id - b.id)
      .map((item: Record<string, number | string>, index: number) => {
        item.name = index ? `Version ${index}` : ORIGINAL_VERSION;

        return item;
      });

    groupedProofItems[proofItemId].sort(
      (a: Record<string, number>, b: Record<string, number>) => b.id - a.id
    );
  });

  return {
    proofItemIds,
    groupedProofItems,
  };
};

/**
 * Method to get the design licensing status
 *
 * @param {object} design - Design object
 */
export const getDesignLicensingStatus = (design: IDesignItem) => {
  const licensingInfo: IDesignLicensing = design?.design?.designLicensing as IDesignLicensing;

  if (licensingInfo) {
    const greekStatus: string = licensingInfo?.greek_status;
    const collegiateStatus: string = licensingInfo.collegiate_status;

    const status = getFinalDesignLicenseStatus(
      greekStatus as DesignLicenseStatusTypes,
      collegiateStatus as DesignLicenseStatusTypes
    );

    const licenisngStatusInfo = status
      ? designLicenseTagConfig[status as keyof typeof designLicenseTagConfig]
      : null;

    return {
      overallLicensingStatus: licenisngStatusInfo?.label ?? '',
      overallLicensingStatusSrc: licenisngStatusInfo?.image ?? '',
      overallLicensingStatusClass: licenisngStatusInfo?.class ?? '',
      overallLicensingPillStyles: status
        ? LICENSING_STATUS_PILL_STYLES[status as keyof typeof LICENSING_STATUS_PILL_STYLES]
        : {},
    };
  }

  return design;
};

/**
 * Resolve variant snapshot from design group transformed designs.
 */
export const getVariantSnapshotFromTransformedDesigns = (
  transformedDesigns?: IDesignGroupProductOption[]
): IVariantSnapshot | undefined => {
  if (!transformedDesigns?.length) {
    return undefined;
  }

  return transformedDesigns.find((design) => design.variantSnapshot)?.variantSnapshot;
};

/**
 * Returns the latest-added design group option (highest designId).
 */
export const getLatestDesignGroupOption = <T extends { designId: number }>(
  options: T[] | undefined | null
): T | undefined => {
  if (!options?.length) {
    return undefined;
  }

  return getLatestRecordBasedOnField(options, 'designId') ?? undefined;
};

/**
 * Returns the product and color that contain the latest-added design option.
 * Prefers the color with the highest designId across all products.
 */
export const getLatestDesignGroupSelection = (
  products: IDesignGroupProductOption[] | undefined | null
): {
  product: IDesignGroupProductOption | undefined;
  color: IDesignGroupColorOption | undefined;
} => {
  if (!products?.length) {
    return { product: undefined, color: undefined };
  }

  let latestProduct = products[0];
  let latestColor = getLatestDesignGroupOption(products[0].colors) ?? products[0].colors[0];
  let maxDesignId = latestColor?.designId ?? products[0].designId;

  products.forEach((product) => {
    const productLatestColor = getLatestDesignGroupOption(product.colors) ?? product.colors[0];
    const candidateDesignId = productLatestColor?.designId ?? product.designId;

    if (candidateDesignId > maxDesignId) {
      maxDesignId = candidateDesignId;
      latestProduct = product;
      latestColor = productLatestColor;
    }
  });

  return { product: latestProduct, color: latestColor };
};

/**
 * Parse variants_json into ordered option values for display.
 * Handles both a JSON string and an already-parsed object (backend sends either).
 */
export const getVariantOptionValues = (
  variantsJson?: string | Record<string, string> | null
): string[] => {
  if (!variantsJson) {
    return [];
  }

  if (typeof variantsJson === 'object') {
    return Object.values(variantsJson).filter((value) => value != null && value !== '');
  }

  try {
    const parsed: Record<string, string> = JSON.parse(variantsJson);
    return Object.values(parsed).filter((value) => value != null && value !== '');
  } catch {
    return [];
  }
};

/** True when variant tag would render (e.g. promo goods with Size • Color). */
export const hasDisplayableVariantSnapshot = (variantSnapshot?: IVariantSnapshot): boolean => {
  if (!variantSnapshot?.variants_json) {
    return false;
  }

  return getVariantOptionValues(variantSnapshot.variants_json).length > 0;
};

/**
 * A reusable method to return the deisgn item object for the design page and revision request page.
 *
 * @param designItem - The design item.
 */
export const getDesignItemObjectForDesignPage = (designItem: IDesignItem) => ({
  code: designItem.proof_item_id ?? '',
  status: '',

  /**
   * Design Details
   */
  pdfLink: (designItem.design.pdfLink ?? '') as string,
  proofLinkPrimary: (designItem.design.primaryImage ?? '') as string,
  proofLinkSecondary: (designItem.design.secondaryImage ?? '') as string,
  printType: (designItem.design.printType ?? '') as string,
  /**
   * Apparel Details
   */
  proofTitle: designItem.apparel?.name
    ? designItem.apparel?.name ?? ''
    : designItem.apparel?.brand ?? '',
  hexCode: designItem.apparel ? (designItem.apparel.hexCode as string) : '',
  isFlashEligible: designItem.apparel ? (designItem.apparel.isFlashEligible as boolean) : false,
  favoriteCount: designItem.apparel ? (designItem.apparel.favoriteCount as number) : 0,
  styleCode: designItem.apparel?.styleCode ?? '',
  color: designItem.apparel?.color ?? '',
  proofDescription: designItem.apparel?.description ?? '',
  externalLink: designItem.apparel?.link ?? '',
  statusDescription: '',
  isExternalApparel: false,
  apparelBackImage: '',
  apparelFrontImage: '',
  proofItemId: designItem.id,
  priceList: designItem.priceList ? designItem.priceList : [],
  supportEmailSent: designItem.supportEmailSent ? designItem.supportEmailSent : false,
  printInfo: designItem.design?.printInfo as string,
  printTypeInfo: designItem.design?.printTypeInfo as IPrintTypeItem[],
  decorMethodInfo: designItem.design?.decorMethodInfo as IDecoratingItem[] /** New Decorating */,
  decoratingMethodInfo: designItem.design?.decoratingMethodInfo as string /** Old Decorating */,
  variantSnapshot:
    designItem.variantSnapshot ??
    getVariantSnapshotFromTransformedDesigns(designItem.designGroupDesigns?.transformedDesigns),
});

/**
 * A method to  get the design data for the design details component.
 *
 * @param designDetails - The design details
 * @param designItem - The design item
 */
export const getProofDataForComponent = (
  designDetails: { name: string; id: number; client_id: number; client_name: string },
  designItem: IDesignItem
) => {
  const newDesignItem: IDesignItem = { ...designItem };

  newDesignItem.componentData = {
    proofId: designDetails.id ?? '',
    proofName: designDetails.name ?? '',
    client: designDetails.client_name ?? '',
    clientId: designDetails.client_id ?? 0,
    showRequestRevision: false,
    fromDesignTool: (designItem.design.fromDesignTool || false) as boolean,
    ...getDesignItemObjectForDesignPage(designItem),
  };

  if (designItem.status === 4) {
    newDesignItem.componentData.status = MostRecentActivityStatus.designDone;
  }

  if (designItem.status !== 4) {
    newDesignItem.componentData.status = MostRecentActivityStatus.inProgress;
  }

  newDesignItem.componentData.isExternalApparel = !!designItem.apparel?.isExternal;

  /**
   * If the apparel isn't external, then we need to add front and back image urls to the data to show them on the UI.
   */
  if (designItem.apparel && !designItem.apparel.isExternal) {
    if (designItem.apparel.brand?.toUpperCase().includes('FRESH PRINTS')) {
      newDesignItem.componentData.apparelFrontImage = designItem.apparel.frontImage || '';
      newDesignItem.componentData.apparelBackImage = designItem.apparel.backImage || '';
    } else if (!shouldUsePromoApparelImages(designItem.variantSnapshot)) {
      const [frontImage, backImage] = getApparelImages(
        designItem.apparel.styleCode ?? '',
        designItem.apparel.hexCode ?? ''
      );

      newDesignItem.componentData.apparelFrontImage = frontImage;
      newDesignItem.componentData.apparelBackImage = backImage;
    }
  }

  return newDesignItem;
};

/**
 * Method to check if it has collegiate or greek licensing
 *
 * @param statusTypes - licensing status types
 * @param collgStatus - collegiate status
 * @param greekStatus - greek status
 */
export const getOrgInfo = (statusTypes: string[], collgStatus: string, greekStatus: string) => {
  let schoolOrNationals = '';

  if (statusTypes.includes(collgStatus) && !statusTypes.includes(greekStatus)) {
    schoolOrNationals = 'your School';
  } else if (!statusTypes.includes(collgStatus) && statusTypes.includes(greekStatus)) {
    schoolOrNationals = '[Org] Nationals';
  } else if (statusTypes.includes(collgStatus) && statusTypes.includes(greekStatus)) {
    schoolOrNationals = '[Org] Nationals & your School';
  }

  return schoolOrNationals;
};

/**
 * A method to get the licensing comment.
 *
 * @param designLicensing - The design licensing details
 * @param status - The design licensing status
 */
export const getLicensingComment = (
  designLicensing: IDesignLicenses,
  status: HqLicensingStatusTypes,
  userOrg: string
): {
  comment: string;
  needChangesTooltip: string;
  internalGreekComment: string;
  internalCollegiateComment: string;
  needsChangesOrgDetails: string;
} => {
  let comment = '';
  let schoolOrNationals = '';
  let needChangesTooltip = '';
  let internalCollegiateComment = '';
  let internalGreekComment = '';
  let needsChangesOrgDetails = '';
  let statusTypes = [];

  const collgStatus = designLicensing.collegiate_status;
  const greekStatus = designLicensing.greek_status;

  if (status === 'licensing_review') {
    statusTypes = ['will_require_approval', 'marks_in_review', 'approval_required'];
    schoolOrNationals = getOrgInfo(statusTypes, collgStatus, greekStatus);
  } else if (status === 'pre_approved') {
    statusTypes = ['marks_pre_approved'];
    schoolOrNationals = getOrgInfo(statusTypes, collgStatus, greekStatus);
  } else if (status === 'approved') {
    statusTypes = ['marks_approved'];
    schoolOrNationals = getOrgInfo(statusTypes, collgStatus, greekStatus);

    if (schoolOrNationals === 'your School') {
      schoolOrNationals = 'Your School';
    }
  } else if (status === 'needs_changes') {
    statusTypes = ['needs_changes'];
    schoolOrNationals = getOrgInfo(statusTypes, collgStatus, greekStatus);

    if (schoolOrNationals === 'your School') {
      schoolOrNationals = 'Your School';
    }

    /** Tooltip to show on the design cards for need changes status */
    needChangesTooltip = needChangesTooltipMsg.replace('Nationals', schoolOrNationals);
    needChangesTooltip = needChangesTooltip.replace('[Org] ', '');

    comment = designLicenseComments[status as HqLicensingStatusTypes];
    comment = schoolOrNationals && comment.replace('Nationals', schoolOrNationals);

    if (
      designLicensing.collegiate_comment_by === 'licensing_team' &&
      designLicensing?.collegiate_comment?.trim()
    ) {
      if (designLicensing.collegiate_status === 'needs_changes') {
        internalCollegiateComment = `"${designLicensing.collegiate_comment.trim()}"`;
        needsChangesOrgDetails = 'collegiate';
      }
    }

    if (
      designLicensing.greek_comment_by === 'licensing_team' &&
      designLicensing?.greek_comment?.trim()
    ) {
      if (designLicensing.greek_status === 'needs_changes') {
        internalGreekComment = `"${designLicensing.greek_comment.trim()}"`;
        needsChangesOrgDetails = 'greek';
      }
    }

    if (internalCollegiateComment && internalGreekComment) {
      comment = schoolOrNationals && comment.replace('They', 'Nationals');
      needsChangesOrgDetails = 'greek-collegiate';
    }

    if (!internalCollegiateComment && !internalGreekComment) {
      comment = comment.replace('They wrote:', '');
      comment = `${comment}${needsChangesWorkWithYouMsg}`;
    }
  }

  if (status !== 'needs_changes') {
    comment = designLicenseComments[status as HqLicensingStatusTypes];
    comment = schoolOrNationals && comment.replaceAll('Nationals', schoolOrNationals);
  }

  if (status === 'approved') {
    comment = schoolOrNationals && comment.replaceAll('[Org] ', '');
  }

  /** Adding the org name for a collegiate client */
  if (userOrg && comment.includes('[Org]')) {
    comment = comment.replace('[Org]', userOrg);
  } else if (!userOrg && comment.includes('[Org]')) {
    comment = comment.replace('[Org]', '');
  }

  return {
    comment,
    needChangesTooltip,
    internalCollegiateComment,
    internalGreekComment,
    needsChangesOrgDetails,
  };
};

/**
 * A method to  get the design licensing status.
 *
 * @param designLicensing - The design licensing details
 */
export const getDesignLicenseStatus = (
  designLicensing: IDesignLicenses,
  userOrg?: string
): DesignLicenseTagConfigType | null => {
  let returnInfo: DesignLicenseTagConfigType | null = null;
  const dlStatus = getFinalDesignLicenseStatus(
    designLicensing?.greek_status,
    designLicensing?.collegiate_status
  );

  if (dlStatus) {
    const {
      comment,
      needChangesTooltip,
      internalCollegiateComment,
      internalGreekComment,
      needsChangesOrgDetails,
    } = getLicensingComment(designLicensing, dlStatus, userOrg || '');

    returnInfo = {
      image: designLicenseTagConfig[dlStatus as keyof typeof designLicenseTagConfig].image,
      theme: designLicenseTagConfig[dlStatus as keyof typeof designLicenseTagConfig].theme,
      label: designLicenseTagConfig[dlStatus as keyof typeof designLicenseTagConfig].label,
      popoverStyles:
        LICENSING_STATUS_PILL_STYLES[dlStatus as keyof typeof LICENSING_STATUS_PILL_STYLES],
      comment,
      needChangesTooltip,
      internalGreekComment,
      internalCollegiateComment,
      needsChangesOrgDetails,
    };
  }

  return returnInfo;
};

/**
 * Method to add the licensing information to the desig item.
 *
 * @param designItems - The design items.
 */
export const getDesignSpecificInfo = (
  designDetails: {
    name: string;
    id: number;
    client_id: number;
    client_name: string;
  },
  designItems: IDesignItem[],
  userOrg: string
) => {
  const newDesignItems: IDesignItem[] = [];

  const completeProofItems = designItems.filter((item: IDesignItem) => item.status === 4);
  const incompleteProofItems = designItems.filter((item: IDesignItem) => item.status !== 4);

  designItems.forEach((designItem: IDesignItem) => {
    designItem.licensingInfo = designItem?.design?.designLicensing
      ? getDesignLicenseStatus(designItem?.design?.designLicensing as IDesignLicenses, userOrg)
      : null;

    const updatedDesignItem = getProofDataForComponent(designDetails, designItem);

    if (designItem.id === completeProofItems[0]?.id && !incompleteProofItems.length) {
      updatedDesignItem.componentData.showRequestRevision = true;
    }

    newDesignItems.push(updatedDesignItem);
  });

  return newDesignItems;
};

/**
 * Method to return the slider width.
 *
 * @param screen - The screen (Desktop/Mobile/Tab)
 */
export const getSliderWidth = (screen: string, viewPortData: IViewPortData) => {
  if (viewPortData.width <= 410) {
    return viewPortData.width - 100;
  }

  if (screen === ViewPort.desktop) {
    return 450;
  }

  if (screen === ViewPort.desktopBig) {
    return 490;
  }

  if (screen === ViewPort.desktopSmall) {
    return 340;
  }

  if (screen === ViewPort.mobile) {
    return 300;
  }

  return 400;
};

/**
 * method to form primary and secondary images.
 *
 * @param groupOrderDate - The group order date
 * @param orderItem - The order item
 */
export const getDesignS3Urls = (groupOrderDate: string | null, orderItem: IOrderItemInfo) => {
  if (!groupOrderDate) {
    return {
      primaryImage: orderItem.design.nt_client_front_image
        ? s3FileUrlGetter(orderItem.design.nt_client_front_image)
        : '',
      secondaryImage: orderItem.design.nt_client_back_image
        ? s3FileUrlGetter(orderItem.design.nt_client_back_image)
        : '',
    };
  }
  return {
    primaryImage:
      orderItem.primary_image === 'front'
        ? s3FileUrlGetter(orderItem.design.client_front_image)
        : s3FileUrlGetter(orderItem.design.client_back_image),
    secondaryImage:
      orderItem.primary_image === 'back'
        ? s3FileUrlGetter(orderItem.design.client_front_image)
        : s3FileUrlGetter(orderItem.design.client_back_image),
  };
};

/**
 * Method to return the latest updated proof item from the list of proof items in a design object.
 *
 * @param design - Design object.
 */
export const getLatestUpdatedProofItem = (design: IDesignCard) => {
  const { proofItems } = design;

  const latestUpdatedProofItem = proofItems.reduce(
    (latest: IDesignProofItem | null, item: IDesignProofItem) => {
      if (!latest || new Date(item.proofItemUpdatedAt) > new Date(latest.proofItemUpdatedAt)) {
        return item;
      }

      return latest;
    },
    proofItems[0]
  );

  return latestUpdatedProofItem;
};

/**
 * Method to return the deisgn card information.
 *
 * @param designDetails - Design list.
 */
export const getDesignCardInformation = (designDetails: IDesignCard[]) => {
  const designs: IDesignCard[] = [];
  designDetails
    .filter((item) => item.proofItems?.length)
    .forEach((design: IDesignCard) => {
      const newDesign: IDesignCard = {
        ...design,
        latestDesignItem: {
          id: '',
          proofItemId: '',
          parentId: '',
          proofs: [],
          status: 0,
          primaryImage: '',
          hasExternalApparel: false,
          proofItemUpdatedAt: '',
          designLicenses: {
            id: 0,
            collegiate_status: 'no_marks',
            greek_status: 'no_marks',
            collegiate_comment: '',
            greek_comment: '',
            design_id: 0,
            collegiate_duplicate_item_id: 0,
            greek_duplicate_item_id: 0,
            has_collegiate_mark: 0,
            has_greek_mark: 0,
            collegiate_comment_by: '',
            greek_comment_by: '',
          },
        },
        remainingDesignItemImages: [],
      };
      let latestUpatedProofItem = getLatestUpdatedProofItem(design);

      const designData: IDesignResponseData = {
        proof: { id: design.id, name: design.title, status: 0 },
        proofItems: design.proofItems,
      };

      const { groupedProofItems } = getDesignItems(designData, 'proofItemId');

      /**
       * Get the latest design or proof item from the grouped proof item id list. Because this will have the more information about the proof item object.
       */
      const latestDesignItemFromGroup = groupedProofItems[latestUpatedProofItem.proofItemId];

      const filteredlatestDesignItem = latestDesignItemFromGroup.filter(
        (item: IDesignProofItem) => item.id === latestUpatedProofItem.id
      )[0];

      /** Merge both the latest proof items. */
      latestUpatedProofItem = { ...latestUpatedProofItem, ...filteredlatestDesignItem };

      /** If the status is in progress, we should fetch the apparel image from the API. */
      if (!latestUpatedProofItem.primaryImage) {
        if (!shouldUsePromoApparelImages(latestUpatedProofItem.variantSnapshot)) {
          const [frontImage, backImage] = getApparelImages(
            latestUpatedProofItem.apparelStyleCode ?? '',
            latestUpatedProofItem.apparelHexCode ?? ''
          );

          latestUpatedProofItem.primaryImage = frontImage || backImage;
        }
      }

      newDesign.latestDesignItem = latestUpatedProofItem;

      /** We need to filter the proof items as we are going to show the latest proof item on the card.
       * So we need to filter out the latest one from the array of proof items to show thumbnails on the card */
      const remainingDesignItems = design.proofItems.filter(
        (item) => item.id !== latestUpatedProofItem.id
      );

      /**
       * Find out the remaining design items to show as thumnails on the design card.
       */
      const remainingDesignItemImages: string[] = remainingDesignItems.map((item) => {
        let { primaryImage } = item;

        if (!item.primaryImage) {
          if (!shouldUsePromoApparelImages(item.variantSnapshot)) {
            const [frontImage, backImage] = getApparelImages(
              item.apparelStyleCode ?? '',
              item.apparelHexCode ?? ''
            );

            primaryImage = frontImage || backImage;
          }
        }

        return primaryImage;
      });

      newDesign.remainingDesignItemImages = remainingDesignItemImages;

      designs.push(newDesign);
    });

  return designs;
};

/**
 * Method to add the licensing information to the design group products and their color options.
 *
 * @param designGroupData - The transformed designs from designGroupDesigns
 * @param userOrg - The user's organization
 * @returns The design group products with licensing information attached to their color options
 */
export const getDesignGroupWithLicensingInfo = (
  designGroupData: IDesignGroupProductOption[] | undefined,
  userOrg: string
): IDesignGroupProductOption[] => {
  if (!designGroupData) {
    return [];
  }

  return designGroupData.map((productOption) => ({
    ...productOption,
    colors: productOption.colors.map((colorOption) => ({
      ...colorOption,
      licensingInfo: colorOption?.designLicensing
        ? getDesignLicenseStatus(colorOption.designLicensing, userOrg)
        : null,
    })),
  }));
};
